
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LoadingContextType {
  isPageLoading: boolean;
  pageLoadingMessage: string;
  setPageLoading: (loading: boolean, message?: string) => void;
  isFileUploading: boolean;
  fileUploadProgress: number;
  fileUploadMessage: string;
  setFileUploading: (loading: boolean, message?: string, progress?: number) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pageLoadingMessage, setPageLoadingMessage] = useState('Loading page...');
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [fileUploadMessage, setFileUploadMessage] = useState('Processing file...');

  const setPageLoading = (loading: boolean, message = 'Loading page...') => {
    setIsPageLoading(loading);
    setPageLoadingMessage(message);
  };

  const setFileUploading = (loading: boolean, message = 'Processing file...', progress = 0) => {
    setIsFileUploading(loading);
    setFileUploadMessage(message);
    setFileUploadProgress(progress);
  };

  return (
    <LoadingContext.Provider
      value={{
        isPageLoading,
        pageLoadingMessage,
        setPageLoading,
        isFileUploading,
        fileUploadProgress,
        fileUploadMessage,
        setFileUploading,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};
